import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Server, ShieldCheck, KeyRound } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const forced = !!user?.must_change_password;
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        new_password: form.next,
        ...(forced ? {} : { current_password: form.current }),
      });
      const u = await refresh();
      toast.success("Password updated");
      navigate(u?.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0b1020] text-white p-12 relative overflow-hidden">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-primary grid place-items-center"><Server className="h-5 w-5" /></div>
          <span className="font-display font-extrabold text-lg tracking-tight">HMG Linux Lab</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display font-black text-4xl xl:text-5xl leading-[1.05] tracking-tight">Secure your account.</h1>
          <p className="mt-5 text-white/70 text-base leading-relaxed">
            {forced ? "You signed in with a temporary password. Choose a new password to continue." : "Update your account password."}
          </p>
        </div>
        <div className="text-white/40 text-xs relative z-10">Development sandbox • Isolated per session</div>
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 text-primary text-sm mb-2">
            {forced ? <ShieldCheck className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
            {forced ? "Password change required" : "Change password"}
          </div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Set a new password</h2>
          <p className="text-muted-foreground text-sm mt-1">{user?.email}</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="change-password-form">
            {!forced && (
              <div className="space-y-1.5">
                <Label htmlFor="current">Current password</Label>
                <Input id="current" type="password" data-testid="cp-current" value={form.current} onChange={set("current")} required placeholder="Current password" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="next">New password</Label>
              <Input id="next" type="password" data-testid="cp-new" value={form.next} onChange={set("next")} required placeholder="At least 6 characters" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input id="confirm" type="password" data-testid="cp-confirm" value={form.confirm} onChange={set("confirm")} required placeholder="Re-enter new password" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="cp-submit">
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>

          <button className="mt-5 text-sm text-muted-foreground hover:text-foreground transition-colors" onClick={() => { logout(); navigate("/"); }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
